import { useRef } from 'react';
import { useSelector } from 'react-redux';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectMatchedTaxonomyEntry } from 'src/redux/content/selectors/selectMatchedTaxonomyEntry';
import type { TaxonomyHeaderContent } from 'src/redux/content/selectors/selectTaxonomyHeaderContent';
import { selectTaxonomyHeaderContent } from 'src/redux/content/selectors/selectTaxonomyHeaderContent';

/**
 * Resolves the taxonomy header content for the current URL, bridging across the
 * client-side content sync to avoid layout shift.
 *
 * While the content for a matched entry is still syncing, the previously
 * confirmed content is held rather than collapsing or flashing empty. The
 * bridge is gated on the content sync (not the products request, which can
 * settle first) and on the URL actually mapping to an entry — a URL with no
 * matching entry has nothing to wait for, so the (empty) current result is
 * returned instead of stale content.
 */
export const useTaxonomyHeaderContent = (): TaxonomyHeaderContent => {
  const content = useSelector(selectTaxonomyHeaderContent);
  const isContentSyncing = useSelector(
    (state: ReduxApplicationState) => state.content.syncing === true,
  );
  const hasMatchedEntry = useSelector(
    (state: ReduxApplicationState) => selectMatchedTaxonomyEntry(state) !== null,
  );

  const lastConfirmedContent = useRef(content);

  if (!isContentSyncing && content.heading) {
    lastConfirmedContent.current = content;
  }

  const shouldBridge = isContentSyncing && hasMatchedEntry && !content.heading;

  return shouldBridge ? lastConfirmedContent.current : content;
};
